import { supabase } from '../../../shared/lib/supabaseClient';

// `advisory` is { grade_level, section }. Everything here is read-only, and what comes back is
// limited by row security to the signed-in adviser's advisees.

export async function getAdvisoryStudents({ grade_level, section }) {
  const { data, error } = await supabase
    .from('students')
    .select('id, lrn, firstname, lastname, middlename')
    .eq('grade_level', grade_level)
    .eq('section', section)
    // Students still waiting for super admin approval aren't part of the class yet.
    .eq('approved', true)
    .order('lastname', { ascending: true })
    .order('firstname', { ascending: true });
  if (error) throw error;
  return data;
}

// The subjects taught to the advisory section: same grade level, with the section assigned to them.
export async function getAdvisorySubjects({ grade_level, section }) {
  const { data, error } = await supabase
    .from('subjects')
    .select('id, name, subject_sections!inner(section)')
    .eq('grade_level', grade_level)
    .eq('subject_sections.section', section)
    .order('name', { ascending: true });
  if (error) throw error;
  return data;
}

// The current class secretary among these students, or null if none is set.
export async function getAdvisorySecretary(studentIds) {
  if (studentIds.length === 0) return null;

  const { data, error } = await supabase
    .from('class_secretaries')
    .select('student_id')
    .in('student_id', studentIds);
  if (error) throw error;
  return data[0]?.student_id ?? null;
}

// Assigns `studentId` as class secretary, replacing `previousId` if one was already set — a
// section has at most one at a time. RLS only lets this through for the student's own adviser.
export async function setClassSecretary(studentId, previousId) {
  if (previousId && previousId !== studentId) {
    const { error: deleteError } = await supabase.from('class_secretaries').delete().eq('student_id', previousId);
    if (deleteError) throw deleteError;
  }
  const { error } = await supabase.from('class_secretaries').upsert({ student_id: studentId });
  if (error) throw error;
}

export async function removeClassSecretary(studentId) {
  const { error } = await supabase.from('class_secretaries').delete().eq('student_id', studentId);
  if (error) throw error;
}

// Term grades keyed by `${studentId}|${subjectId}`.
export async function getAdvisoryGrades(studentIds, term) {
  if (studentIds.length === 0) return {};

  const { data, error } = await supabase
    .from('subject_grades')
    .select('student_id, subject_id, term_grade')
    .eq('term', term)
    .in('student_id', studentIds);
  if (error) throw error;
  return Object.fromEntries(data.map((r) => [`${r.student_id}|${r.subject_id}`, r.term_grade]));
}

// One student's term grades across all terms, keyed by `${subjectId}|${term}`.
export async function getStudentGrades(studentId) {
  const { data, error } = await supabase
    .from('subject_grades')
    .select('subject_id, term, term_grade')
    .eq('student_id', studentId);
  if (error) throw error;
  return Object.fromEntries(data.map((r) => [`${r.subject_id}|${r.term}`, r.term_grade]));
}

// Attendance counts keyed by student id: one subject when `subjectId` is given, otherwise all subjects
// added together. `attendance_pct` is present / (present + late + absent); only present days count as attended, and excused days are
// left out, and it's null when the student has no counted days.
export async function getAdvisoryAttendance(studentIds, term, subjectId = '') {
  if (studentIds.length === 0) return {};

  let query = supabase
    .from('student_attendance_summary')
    .select('student_id, present, late, absent, excused')
    .eq('term', term)
    .in('student_id', studentIds);
  if (subjectId) query = query.eq('subject_id', subjectId);

  const { data, error } = await query;
  if (error) throw error;

  const totals = {};
  data.forEach((row) => {
    const t = (totals[row.student_id] ??= { present: 0, late: 0, absent: 0, excused: 0 });
    t.present += Number(row.present);
    t.late += Number(row.late);
    t.absent += Number(row.absent);
    t.excused += Number(row.excused);
  });

  Object.values(totals).forEach((t) => {
    const counted = t.present + t.late + t.absent;
    t.attendance_pct = counted > 0 ? (t.present / counted) * 100 : null;
  });
  return totals;
}
